// ./src/components/features/CreateEventWizard/steps/WebsiteStep.tsx
//
// "What's on your event page?" — the wizard's website step (ROADMAP A2b #62 P6(d)–(d3), owner 2026-09-30: a sign-up list
// made in the wizard never reached the site, because a block shows only when a page carries it).
//
// The host writes the event's DESCRIPTION (d2 — rich text with the full-screen editor; it becomes the About block, an
// ordinary rich-text block), ticks the blocks for the home page — one per chosen feature, ticked when it already has
// something to show, and the general ones (owner's defaults: About, Countdown, Location map, Weather, Contact us, Share) —
// and drags the home page into the order they want (d3): the blocks already there and the ones being added, as one list.
// It is all written on Next / Finish (`CreateEventWizard.saveWebsite` → `applyWebsitePlan`).
//
// ⚠ Never removes: a block already on any page is shown ticked and fixed ("already on your event page").
// ⚠ The site's state is this step's own READ: not read yet → skeleton, failed → the error with a retry (never an empty
//   checklist that would add duplicates of blocks it could not see).

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge, Box, Button, Card, Checkbox, Group, Paper, Skeleton, Stack, Text, ThemeIcon } from '@mantine/core';
import { DragDropContext, Draggable, DropResult, Droppable } from '@hello-pangea/dnd';
import { OpResult } from '@sdflc/api-helpers';
import { STATUSES } from '@sdflc/utils';
import { useTranslation } from 'react-i18next';

import { blockIcon, blockKindLabel, textKindOf } from '@em/blocks';
import { Event, EventService, EventFeatureUsage, EventTimeSlotService, PageWidget } from '@/utils/api';
import { AppIcon, ErrorAlert } from '@/components/generic';
import { PolicyTextEditor } from '@/components/features/Policies/PolicyTextEditor/PolicyTextEditor';

import { useEventWizard } from '../WizardContext';
import { SCHEDULE_MODE } from '../wizardTypes';
import { WEBSITE_BLOCK_OPTIONS, WebsiteBlockKey, WebsiteBlockRow, formatOfTextKind, homeOrder, planWebsiteBlocks, websiteKeysToAdd } from '../websiteBlocks';
import { isGiftList } from '../giftRegistry';
import { WebsiteSiteState, readWebsiteState } from '../websiteBlocksApply';

interface WebsiteStepData {
  event: Event;
  withData: string[];
  site: WebsiteSiteState;
  /** The event has at least one session (an agenda's Schedule block shows nothing without). */
  hasSessions: boolean;
}

/** No merge variables in a description — the site prints the text as written. */
const NO_VARIABLES: never[] = [];

const WebsiteStepBody: React.FC<{ eventId: string }> = ({ eventId }) => {
  const { t } = useTranslation(['events', 'common']);
  const { draft, patchDraft, saving } = useEventWizard();

  const [data, setData] = useState<WebsiteStepData | null>(null);
  const [readError, setReadError] = useState<OpResult | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setReadError(null);

    const [eventRes, usageRes, site, slotsRes] = await Promise.all([
      EventService.get(eventId),
      EventService.featureUsage(eventId),
      readWebsiteState(eventId, draft.aboutWidgetId),
      EventTimeSlotService.list({ eventId: [eventId] }, { page: 1, pageSize: 1 }),
    ]);

    setLoading(false);

    if (!eventRes.didSucceed()) {
      setReadError(eventRes);

      return;
    }

    if (site instanceof OpResult) {
      setReadError(site);

      return;
    }

    // ⓘ Feature usage only decides what starts TICKED — a failed read ticks no feature block rather than failing the step.
    const usage = usageRes.didSucceed() ? (usageRes.getDataFirst() as EventFeatureUsage | undefined) : undefined;

    // ⓘ A failed sessions read offers no Schedule block tick: it is ticked only when sessions are KNOWN to exist.
    const hasSessions = slotsRes.didSucceed() && Array.isArray(slotsRes.data) && slotsRes.data.length > 0;

    setData({ event: eventRes.getDataFirst() as Event, withData: usage?.withData ?? [], site, hasSessions });
    // ⓘ Read once per visit: the About block's id is the one this wizard made, and a later change of it is its own save.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  useEffect(() => {
    void load();
  }, [load]);

  // The About block this wizard made, when it is still on the site.
  const aboutWidget: PageWidget | null = useMemo(
    () => (data && draft.aboutWidgetId ? data.site.widgets.find((w) => w.id === draft.aboutWidgetId) ?? null : null),
    [data, draft.aboutWidgetId],
  );
  const description = draft.description ?? '';
  const descriptionFormat = draft.descriptionFormat || 'richtext';

  const rows: WebsiteBlockRow[] = useMemo(
    () =>
      data
        ? planWebsiteBlocks({
            featuresOn: draft.featuresOn ?? [],
            withData: data.withData,
            hasDescription: !!description.trim(),
            hasStart: !!data.event.startsAt,
            hasVenue: !!data.event.venueId,
            // ⚠ WITH sessions (any mode but a repeating one, whose slots are its dates) — none publishes an empty block.
            hasSessions: data.event.scheduleMode !== SCHEDULE_MODE.RECURRING && data.hasSessions,
            onSite: data.site.onSite,
          })
        : [],
    [data, draft.featuresOn, description],
  );

  // The ticks from the defaults — once, so the host's own ticks (and a reload mid-step) are never overwritten. The
  // description is what the About block SAYS, read on every visit until the host types (review, 2026-09-30: text edited in
  // the page editor since was shown stale, and one keystroke wrote the stale copy back over it).
  useEffect(() => {
    if (!data) {
      return;
    }

    const patch: Parameters<typeof patchDraft>[0] = {};

    if (draft.description == null && !aboutWidget) {
      patch.description = '';
      patch.descriptionFormat = 'richtext';
      patch.descriptionEdited = false;
    }

    if (aboutWidget && !draft.descriptionEdited) {
      const onPage = aboutWidget.content ?? '';
      const format = formatOfTextKind(textKindOf(aboutWidget));

      if (draft.description !== onPage || draft.descriptionFormat !== format) {
        patch.description = onPage;
        patch.descriptionFormat = format;
      }
    }

    if (draft.websiteBlocks == null) {
      patch.websiteBlocks = rows.filter((r) => r.defaultChecked && !r.onSite).map((r) => r.option.key);
    }

    if (Object.keys(patch).length > 0) {
      patchDraft(patch);
    }
  }, [data, rows, aboutWidget, draft.description, draft.descriptionFormat, draft.descriptionEdited, draft.websiteBlocks, patchDraft]);

  // ⓘ Memoised: a fresh `[]` on every render would change what the memos below depend on (#129).
  const ticked = useMemo(() => draft.websiteBlocks ?? [], [draft.websiteBlocks]);

  const toggle = (key: string, on: boolean) => {
    patchDraft({ websiteBlocks: on ? [...ticked.filter((k) => k !== key), key] : ticked.filter((k) => k !== key) });
  };

  // ── The home page's order (d3) — what is there, then what is being added, as the host arranged it ──────────────────
  const adding = useMemo(() => websiteKeysToAdd(rows, ticked), [rows, ticked]);
  const order = useMemo(
    () => (data ? homeOrder(data.site.homeWidgets.map((w) => String(w.id)), adding, draft.websiteOrder) : []),
    [data, adding, draft.websiteOrder],
  );

  const onDragEnd = (result: DropResult) => {
    const { source, destination } = result;

    if (!destination || destination.index === source.index) {
      return;
    }

    const next = [...order];
    const [moved] = next.splice(source.index, 1);

    next.splice(destination.index, 0, moved);
    patchDraft({ websiteOrder: next });
  };

  const noteOf = (row: WebsiteBlockRow): string | null => {
    if (row.onSite) {
      return t('events:wizard.website.onSite', { defaultValue: 'Already on your event page.' });
    }

    if (row.missing === 'description') {
      return t('events:wizard.website.needsDescription', { defaultValue: 'Write a description above to show it.' });
    }

    if (row.missing === 'start') {
      return t('events:wizard.website.needsStart', { defaultValue: 'Needs a start date.' });
    }

    if (row.missing === 'venue') {
      return t('events:wizard.website.needsVenue', { defaultValue: 'Needs a venue.' });
    }

    if (row.missing === 'sessions') {
      return t('events:wizard.website.needsSessions', { defaultValue: 'Needs sessions — add them on the Schedule step.' });
    }

    if (row.empty) {
      return t('events:wizard.website.empty', {
        defaultValue: 'Nothing to show yet. Add some first — you can then put it on the page from the Website page.',
      });
    }

    return null;
  };

  // #140 — a Sign-up list chosen on the Gifts step is the GIFT list here too (its block is titled so on save).
  const giftList = isGiftList(draft);
  const optionLabel = (key: WebsiteBlockKey, fallback: string): string =>
    key === 'signup' && giftList
      ? t('events:wizard.gifts.listBlockTitle', { defaultValue: 'Gift list' })
      : t(`events:wizard.website.block.${key}`, { defaultValue: fallback });

  const renderRow = (row: WebsiteBlockRow) => {
    const note = noteOf(row);
    const checked = row.onSite || (row.tickable && ticked.includes(row.option.key));

    return (
      <Paper key={row.option.key} withBorder radius="md" p="sm">
        <Group gap="sm" wrap="nowrap" align="flex-start">
          <Checkbox
            size="md"
            mt={6}
            checked={checked}
            disabled={saving || !row.tickable}
            onChange={(e) => toggle(row.option.key, e.currentTarget.checked)}
            aria-label={optionLabel(row.option.key, row.option.label)}
          />
          <ThemeIcon variant="light" size={34} radius="md">
            <AppIcon iconName={row.option.icon} size={17} />
          </ThemeIcon>
          <Box style={{ minWidth: 0 }}>
            <Text size="md" fw={600}>
              {optionLabel(row.option.key, row.option.label)}
            </Text>
            <Text size="md" c="dimmed">
              {row.option.key === 'signup' && giftList
                ? t('events:wizard.gifts.listBlockHint', { defaultValue: 'Guests pick a gift, so nothing is bought twice.' })
                : t(`events:wizard.website.blockHint.${row.option.key}`, { defaultValue: row.option.description })}
            </Text>
            {note ? (
              <Text size="md" c={row.onSite ? 'teal.8' : 'dimmed'}>
                {note}
              </Text>
            ) : null}
          </Box>
        </Group>
      </Paper>
    );
  };

  /** One line of the order list: a block already on the home page, or one being added. */
  const renderOrderItem = (item: string, index: number) => {
    const widget = item.startsWith('w:') && data ? data.site.homeWidgets.find((w) => w.id === item.slice(2)) ?? null : null;
    const option = item.startsWith('k:') ? WEBSITE_BLOCK_OPTIONS.find((o) => o.key === (item.slice(2) as WebsiteBlockKey)) ?? null : null;

    if (!widget && !option) {
      return null;
    }

    const label = widget
      ? widget.title || blockKindLabel(widget)
      : option ? optionLabel(option.key, option.label) : '';
    const icon = widget ? blockIcon(widget) : option?.icon ?? 'LayoutGrid';
    const hidden = !!widget && widget.status !== STATUSES.ACTIVE;

    return (
      <Draggable key={item} draggableId={item} index={index} isDragDisabled={saving}>
        {(prov, snapshot) => (
          <Card
            ref={prov.innerRef}
            {...prov.draggableProps}
            withBorder
            radius="md"
            padding="sm"
            style={{ ...prov.draggableProps.style, boxShadow: snapshot.isDragging ? 'var(--mantine-shadow-md)' : undefined }}
          >
            <Group gap="sm" wrap="nowrap">
              <Box
                {...prov.dragHandleProps}
                c="dimmed"
                style={{ cursor: 'grab', display: 'flex' }}
                aria-label={t('events:wizard.website.dragHandle', { defaultValue: 'Drag to move {{name}}', name: label })}
              >
                <AppIcon iconName="GripHorizontal" size={20} />
              </Box>
              <AppIcon iconName={icon} size={18} />
              <Text fw={600} size="md" lineClamp={1} style={{ flex: 1, minWidth: 0 }}>
                {label}
              </Text>
              {option ? (
                <Badge variant="light" color="brand" size="md">
                  {t('events:wizard.website.orderNew', { defaultValue: 'New' })}
                </Badge>
              ) : null}
              {hidden ? (
                <Badge variant="light" color="gray" size="md">
                  {t('events:website.draft', { defaultValue: 'Draft' })}
                </Badge>
              ) : null}
            </Group>
          </Card>
        )}
      </Draggable>
    );
  };

  const renderOrder = () => {
    if (!data || order.length < 2) {
      return null;
    }

    return (
      <Stack gap="xs">
        <Text size="md" fw={600}>
          {t('events:wizard.website.orderTitle', { defaultValue: 'Order on your home page' })}
        </Text>
        <Text size="md" c="dimmed">
          {t('events:wizard.website.orderHint', {
            defaultValue: 'Drag the blocks into the order guests should see them, below the cover and the date.',
          })}
        </Text>
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="website-home-order">
            {(provided) => (
              <Stack gap="xs" ref={provided.innerRef} {...provided.droppableProps}>
                {order.map(renderOrderItem)}
                {provided.placeholder}
              </Stack>
            )}
          </Droppable>
        </DragDropContext>
      </Stack>
    );
  };

  const renderChecklist = () => {
    if (!data && loading) {
      return <Skeleton height={220} radius="md" />;
    }

    if (readError || !data) {
      return (
        <Stack gap="xs">
          <ErrorAlert
            error={readError}
            title={t('events:wizard.website.loadFailed', { defaultValue: 'Could not read your event page' })}
            page="Create event"
            operation="eventPageList"
          />
          <Group>
            <Button variant="light" size="md" leftSection={<AppIcon iconName="Refresh" size={16} />} onClick={() => void load()}>
              {t('common:retry', { defaultValue: 'Try again' })}
            </Button>
          </Group>
        </Stack>
      );
    }

    const featureRows = rows.filter((r) => !!r.option.feature);
    const generalRows = rows.filter((r) => !r.option.feature);
    // ⓘ Blocks only ever go on a PUBLISHED page — when there is none, a published "Home" is created for them.
    const createsHome = !data.site.homePage;

    return (
      <Stack gap="md">
        {createsHome ? (
          <Text size="md" c="dimmed">
            {t('events:wizard.website.createsHome', {
              defaultValue: 'Your event has no published page yet, so a Home page is created for these blocks.',
            })}
          </Text>
        ) : null}
        {featureRows.length > 0 ? (
          <Stack gap="xs">
            <Text size="md" fw={600}>
              {t('events:wizard.website.fromEvent', { defaultValue: 'From your event' })}
            </Text>
            {featureRows.map(renderRow)}
          </Stack>
        ) : null}
        <Stack gap="xs">
          <Text size="md" fw={600}>
            {t('events:wizard.website.general', { defaultValue: 'Also on the page' })}
          </Text>
          {generalRows.map(renderRow)}
        </Stack>
        {renderOrder()}
      </Stack>
    );
  };

  // ⚠ An About that is an OLDER live Overview block (not one this wizard made) has no text of its own to edit here.
  const aboutIsLegacy = !!data?.site.onSite.has('about') && !aboutWidget;

  return (
    <Stack gap="lg">
      <Text size="md" c="dimmed">
        {t('events:wizard.website.intro', {
          defaultValue:
            'Choose what your event page shows and in what order. Guests see the cover and the date at the top, then these blocks. You can change them later on the Website page.',
        })}
      </Text>

      {aboutIsLegacy ? (
        <Text size="md" c="dimmed">
          {t('events:wizard.website.aboutLegacy', {
            defaultValue: 'Your About block shows the event’s Overview. Edit it, or switch it to its own text, on the Website page.',
          })}
        </Text>
      ) : (
        <PolicyTextEditor
          label={t('events:wizard.website.description', { defaultValue: 'Description' })}
          description={
            aboutWidget
              ? t('events:wizard.website.descriptionOnPage', { defaultValue: 'What your About block says. Changes are saved to it when you continue.' })
              : t('events:wizard.website.descriptionHint', { defaultValue: 'What your event is about — shown in the About block of your event page.' })
          }
          format={descriptionFormat}
          onFormatChange={(format) => patchDraft({ descriptionFormat: format, descriptionEdited: true })}
          value={description}
          onChange={(value) => patchDraft({ description: value, descriptionEdited: true })}
          placeholder={t('events:wizard.website.descriptionPh', { defaultValue: 'Join us for…' })}
          variables={NO_VARIABLES}
        />
      )}
      {/* ⚠ An emptied description is NOT written: the About block keeps its text (removing a block is the editor's job). */}
      {aboutWidget && !description.trim() ? (
        <Text size="md" c="dimmed">
          {t('events:wizard.website.descriptionEmptied', {
            defaultValue: 'An empty description leaves your About block as it is. To take it off the page, remove it on the Website page.',
          })}
        </Text>
      ) : null}

      {renderChecklist()}

      <Text size="md" c="dimmed">
        {t('events:wizard.website.whenAdded', {
          defaultValue: 'The ticked blocks are added, and the page put in this order, when you continue. Nothing on your pages is removed.',
        })}
      </Text>
    </Stack>
  );
};

export const WebsiteStep: React.FC = () => {
  const { t } = useTranslation(['events']);
  const { createdEventId } = useEventWizard();

  if (!createdEventId) {
    return (
      <Text size="md" c="dimmed">
        {t('events:wizard.tickets.noEvent', { defaultValue: 'Your event is created at the end of the Guests step.' })}
      </Text>
    );
  }

  return <WebsiteStepBody eventId={createdEventId} />;
};
